import { useNavigate } from "react-router-dom";
import Footer from "@/components/Footer";
import AppleSection from "@/components/AppleSection";
import TimelineSection from "@/components/TimelineSection";
import FutureSection from "@/components/FutureSection";
import AnimatedCounter from "@/components/AnimatedCounter";
import NuestraHistoriaSection from "@/components/NuestraHistoriaSection";
import CrisisYReinvencionSection from "@/components/CrisisYReinvencionSection";
import { useSEO } from "@/hooks/useSEO";

const values = [
  {
    title: "Hablamos claro",
    description: "Sin jerga tech. Te explicamos todo como si fuéramos amigos.",
  },
  {
    title: "Clientes de largo recorrido",
    description: "Del módem de 128k al Cloud. Algunos llevan con nosotros desde el principio.",
  },
  {
    title: "Una llamada > 47 emails",
    description: "Preferimos hablar. Es más rápido y más humano.",
  },
  {
    title: "Te acompañamos en cada transición",
    description: "Del papel al digital, del servidor al Cloud, y ahora a la IA.",
  },
  {
    title: '"No vendemos humo"',
    description: "Probamos cada herramienta. Solo recomendamos lo que funciona.",
  },
];

const testimonials = [
  {
    quote: "Llevan con nosotros desde el principio. Han visto crecer la empresa y siempre han estado ahí.",
    author: "M. Rufo",
    role: "CEO, Maranu",
  },
  {
    quote: "Lo que más valoro es que hablan nuestro idioma. No necesitas ser técnico para entenderles.",
    author: "M. Varón",
    role: "Director Financiero, Content ED",
  },
  {
    quote: "La tranquilidad de saber que cuando llamas, te cogen.",
    author: "A. Sánchez",
    role: "RRHH, PM",
  },
];

const Nosotros = () => {
  const navigate = useNavigate();

  useSEO({
    title: "Sobre Nosotros - 20+ años en tecnología | Globaltis IA",
    description: "Desde 2003 ayudando a PYMEs con tecnología. De los Mac a la IA. Conoce nuestra historia, valores y por qué más de 400 empresas confían en nosotros.",
  });

  return (
    <div className="min-h-screen">
      {/* Hero Section - Apple Style */}
      <section className="relative flex items-center justify-center bg-white dark:bg-black pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-8 text-center">
          <p className="text-xl md:text-2xl font-light text-muted-foreground mb-8 animate-fade-in">
            Desde 2003
          </p>
          
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight text-foreground mb-8 animate-fade-in">
            De los Mac a la IA.<br />
            <span className="text-muted-foreground">+20 años llevando tecnología a PYMEs.</span>
          </h1>
          
          <div className="max-w-3xl mx-auto space-y-6 mb-12 animate-fade-in">
            <p className="text-xl md:text-2xl font-light text-muted-foreground">
              Cuando empezamos, integrábamos Macs en departamentos gráficos con el mundo PC.
            </p>
            <p className="text-xl md:text-2xl font-light text-foreground">
              Hoy ayudamos a PYMEs a implementar inteligencia artificial.
            </p>
          </div>
          
          <button
            onClick={() => {
              document.getElementById('nuestra-historia')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-block px-12 py-5 text-xl rounded-full bg-foreground text-background hover:opacity-80 transition-all duration-300 font-medium"
          >
            Conocer nuestra historia
          </button>
        </div>
      </section>

      {/* Counters Section - White */}
      <AppleSection background="white" padding="normal">
        <div className="grid grid-cols-3 gap-12 md:gap-16 max-w-3xl mx-auto">
          <AnimatedCounter end={20} prefix="+" label="años en tecnología" />
          <AnimatedCounter end={400} prefix="+" label="PYMEs ayudadas" />
          <AnimatedCounter end={4} label="capas de servicio" />
        </div>
      </AppleSection>

      {/* Nuestra Historia Section - Gray */}
      <AppleSection background="gray" padding="large" id="nuestra-historia">
        <NuestraHistoriaSection />
      </AppleSection>

      {/* Crisis y Reinvención Section - White */}
      <AppleSection background="white" padding="large">
        <CrisisYReinvencionSection />
      </AppleSection>

      {/* Timeline Section - Gray */}
      <AppleSection background="gray" padding="large" id="timeline">
        <div className="text-center mb-20">
          <p className="text-sm font-light tracking-[0.2em] text-muted-foreground uppercase mb-6">
            Contexto tecnológico
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-foreground leading-tight mb-6">
            23 años en tecnología.<br />
            <span className="text-muted-foreground">Y los que vendrán.</span>
          </h2>
        </div>
        
        <TimelineSection isDark={false} />
      </AppleSection>

      {/* Future Section - Black */}
      <AppleSection background="black" padding="large">
        <FutureSection />
      </AppleSection>

      {/* Values Section - White */}
      <AppleSection background="white" padding="large">
        <div className="text-center mb-20">
          <p className="text-sm font-light tracking-[0.2em] text-muted-foreground uppercase mb-6">
            Nuestra filosofía
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-foreground leading-tight">
            Lo que NO ha cambiado.
          </h2>
        </div>

        <div className="max-w-4xl mx-auto space-y-0">
          {values.map((value, index) => (
            <div
              key={value.title}
              className="py-10 md:py-14 border-b border-border/30 last:border-b-0"
            >
              <div className="flex items-start gap-8">
                <div className="text-4xl md:text-5xl font-bold text-muted-foreground/30">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-light text-foreground mb-3">
                    {value.title}
                  </h3>
                  <p className="text-lg md:text-xl font-light text-muted-foreground">
                    {value.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </AppleSection>

      {/* Testimonials Section - Gray */}
      <AppleSection background="gray" padding="large">
        <div className="text-center mb-20">
          <p className="text-sm font-light tracking-[0.2em] text-muted-foreground uppercase mb-6">
            Lo que dicen de nosotros
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-foreground leading-tight">
            Clientes que nos conocen<br />
            <span className="text-muted-foreground">desde hace años.</span>
          </h2>
        </div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {testimonials.map((testimonial) => (
            <div key={testimonial.author} className="text-center md:text-left">
              <blockquote className="text-xl md:text-2xl font-light text-foreground leading-relaxed mb-8">
                "{testimonial.quote}"
              </blockquote>
              <div>
                <div className="text-lg font-medium text-foreground">
                  {testimonial.author}
                </div>
                <div className="text-base font-light text-muted-foreground">
                  {testimonial.role}
                </div>
              </div>
            </div>
          ))}
        </div>
      </AppleSection>

      {/* CTA Section - Black */}
      <AppleSection background="black" padding="large">
        <div className="text-center max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-white leading-tight mb-8">
            ¿+20 años no te convencen?
          </h2>
          <p className="text-xl md:text-2xl font-light text-white/60 mb-12">
            Mejor hablamos y te contamos los secretos. Sin compromiso.
          </p>
          <button 
            onClick={() => navigate('/contacto')}
            className="group inline-flex items-center gap-4 text-xl md:text-2xl font-light text-white hover:opacity-60 transition-opacity"
          >
            <span>Agendar una charla</span>
            <span className="text-2xl md:text-3xl group-hover:translate-x-2 transition-transform">→</span>
          </button>
        </div>
      </AppleSection>

      <Footer />
    </div>
  );
};

export default Nosotros;
